import React, { useEffect, useMemo, useReducer, useState } from 'react';
import './App.css';
import { listMacarons, listFlavors, getPricing } from './api';
import { cartReducer, summarize, parseRoute, loadCart, saveCart, browserStorage, money, DEFAULT_TIERS, MAX_ORDER_QUANTITY } from './shop';
import { useCheckout } from './useCheckout';
import { Filters, Grid, ProductPage, Spotlight } from './components/Catalog';
import { Ticker, useReveal } from './components/Photo';
import { SCENES } from './photos';
import { Cart } from './components/Cart';
import { OrderPage } from './components/OrderPage';
import { HelpPage } from './components/HelpPage';

const storage = browserStorage();
const INITIAL_FILTERS = { q: '', flavor: '', tag: '', sort: 'featured', inStock: false };

function useRoute() {
  const [route, setRoute] = useState(() => parseRoute(window.location.hash));
  useEffect(() => {
    const on = () => setRoute(parseRoute(window.location.hash));
    window.addEventListener('hashchange', on);
    return () => window.removeEventListener('hashchange', on);
  }, []);
  return route;
}

// The header turns solid once the hero has scrolled away.
function useScrolled(px = 40) {
  const [scrolled, setScrolled] = useState(false);
  useEffect(() => {
    const on = () => setScrolled(window.scrollY > px);
    on();
    window.addEventListener('scroll', on, { passive: true });
    return () => window.removeEventListener('scroll', on);
  }, [px]);
  return scrolled;
}

// Phones: true while the shop filters sit under the fixed box bar (bottom ~90px of the viewport) or one of them has
// focus (the on-screen keyboard shrinks the viewport), so the bar steps aside instead of covering them.
const BOX_BAR_ZONE = 90;
function useFiltersUnderBoxBar(active) {
  const [under, setUnder] = useState(false);
  useEffect(() => {
    if (!active) { setUnder(false); return undefined; }
    const check = () => {
      const el = document.querySelector('.filters');
      if (!el) { setUnder(false); return; }
      const r = el.getBoundingClientRect();
      const vh = window.visualViewport ? window.visualViewport.height : window.innerHeight;
      const focused = el.contains(document.activeElement) && document.activeElement !== document.body;
      setUnder(focused || (r.bottom > vh - BOX_BAR_ZONE && r.top < vh));
    };
    check();
    window.addEventListener('scroll', check, { passive: true });
    window.addEventListener('resize', check);
    document.addEventListener('focusin', check);
    document.addEventListener('focusout', check);
    const vv = window.visualViewport;
    if (vv) vv.addEventListener('resize', check);
    return () => {
      window.removeEventListener('scroll', check);
      window.removeEventListener('resize', check);
      document.removeEventListener('focusin', check);
      document.removeEventListener('focusout', check);
      if (vv) vv.removeEventListener('resize', check);
    };
  }, [active]);
  return under;
}

const scrollToId = (id) => { const el = document.getElementById(id); if (el) el.scrollIntoView({ behavior: 'smooth' }); };

function Brand({ light }) {
  return (
    <a className={`brand ${light ? 'light' : ''}`} href="#/" aria-label="La Maison des Macarons, home">
      <span className="brand-script">La Maison</span>
      <span className="brand-caps">des Macarons</span>
    </a>
  );
}

function App() {
  const route = useRoute();
  const scrolled = useScrolled();
  const pastHero = useScrolled(560); // phones: the box bar would cover the hero's buttons
  const revealRef = useReveal();
  const [catalog, setCatalog] = useState([]); // the whole catalogue: prices and stock for the cart
  const [shown, setShown] = useState([]); // the filtered list
  const [flavors, setFlavors] = useState([]);
  const [tiers, setTiers] = useState(DEFAULT_TIERS);
  const [filters, setFilters] = useState(INITIAL_FILTERS);
  const [loading, setLoading] = useState(true);
  const [initError, setInitError] = useState(null); // the catalogue, flavours: the shop cannot work without them
  const [filterError, setFilterError] = useState(null); // the current search only
  const [attempt, setAttempt] = useState(0);
  const [form, setForm] = useState({ name: '', email: '', pickupDate: '', note: '' }); // survives closing the drawer
  const [cartOpen, setCartOpen] = useState(false);
  const [justPlaced, setJustPlaced] = useState(null);
  const [bump, setBump] = useState(0); // re-keys the box badge so it pops on every add
  const [cart, dispatch] = useReducer(cartReducer, undefined, () => loadCart(storage));

  useEffect(() => saveCart(storage, cart), [cart]);

  const refreshCatalog = () => listMacarons({}).then(setCatalog);

  // Pricing tiers are optional (the defaults match the API); the catalogue and flavours are not.
  useEffect(() => {
    setLoading(true);
    setInitError(null);
    Promise.all([refreshCatalog(), listFlavors().then(setFlavors), getPricing().then((p) => setTiers(p.discountTiers)).catch(() => {})])
      .catch((err) => setInitError(err.message))
      .finally(() => setLoading(false));
  }, [attempt]);

  // Filtering is server-side (the API owns search and sort); a short debounce keeps typing smooth. A newer filter
  // aborts the older request, so a slow old answer can never replace the current list.
  useEffect(() => {
    const ctl = new AbortController();
    const t = setTimeout(() => {
      listMacarons(filters, ctl.signal)
        .then((list) => { setShown(list); setFilterError(null); })
        .catch((err) => { if (!ctl.signal.aborted) setFilterError(err.message); });
    }, 200);
    return () => { clearTimeout(t); ctl.abort(); };
  }, [filters]);

  const checkout = useCheckout(storage, (order) => placed(order));

  const summary = useMemo(() => summarize(cart, catalog, tiers), [cart, catalog, tiers]);
  const add = (m, n) => { dispatch({ type: 'add', id: m.id, quantity: n, stock: m.stock }); setBump((b) => b + 1); };

  const placed = (order) => {
    dispatch({ type: 'clear' });
    setForm({ name: '', email: '', pickupDate: '', note: '' });
    setCartOpen(false);
    setJustPlaced(order);
    refreshCatalog().catch(() => {});
    setFilters({ ...filters }); // reload the shown stock
    window.location.hash = `#/order/${order.id}`;
  };

  const scrollToShop = () => {
    if (route.page !== 'shop') window.location.hash = '#/';
    setTimeout(() => scrollToId('shop'), 0);
  };
  const pickCollection = (tag) => { setFilters({ ...INITIAL_FILTERS, tag }); scrollToShop(); };

  const shopBody = loading ? (
    <div className="loading"><div className="spinner" /><p>Loading today’s macarons…</p></div>
  ) : initError ? (
    <div className="error">
      <h2>Oops! Something went wrong</h2>
      <p>{initError}</p>
      <button type="button" className="btn btn-dark" onClick={() => setAttempt((a) => a + 1)}>Try again</button>
    </div>
  ) : null;

  const home = route.page === 'shop';
  const boxBarWanted = summary.quantity > 0 && !cartOpen && route.page !== 'order' && (!home || pastHero);
  const filtersUnderBar = useFiltersUnderBoxBar(boxBarWanted && home);
  const showBoxBar = boxBarWanted && !filtersUnderBar;
  const bestsellers = catalog.filter((m) => m.tags.includes('bestseller'));
  const [twelve, twentyFour] = [...tiers].sort((a, b) => a.minQuantity - b.minQuantity);

  return (
    <div className={`App ${home ? 'is-home' : 'is-inner'} ${boxBarWanted ? 'has-box-bar' : ''}`} ref={revealRef}>
      {home ? (
        <a className="skip" href="#shop" onClick={(e) => { e.preventDefault(); scrollToShop(); }}>Skip to the shop</a>
      ) : (
        // Inner pages (help, order, macaron) have no #shop: skip to their content. preventDefault keeps the #/ route.
        <a className="skip" href="#main" onClick={(e) => { e.preventDefault(); const m = document.getElementById('main'); if (m) { m.focus(); m.scrollIntoView(); } }}>Skip to content</a>
      )}
      <Ticker className="announce" label="Offers" items={['Baked fresh every morning', 'Free pickup at our counter', `${twelve ? twelve.percent : 10}% off a box of 12`, `${twentyFour ? twentyFour.percent : 15}% off 24 or more`, 'Pay when you collect']} />
      <header className={`header ${scrolled || !home ? 'solid' : ''}`}>
        <div className="header-content">
          <Brand light={home && !scrolled} />
          <nav className="nav" aria-label="Main">
            <button type="button" className="nav-link" onClick={scrollToShop}>Shop</button>
            <button type="button" className="nav-link" onClick={() => pickCollection('bestseller')}>Bestsellers</button>
            <a className="nav-link" href="#visit" onClick={(e) => { e.preventDefault(); scrollToId('visit'); }}>Visit us</a>
            <a className="nav-link" href="#/help">Help</a>
          </nav>
          <button type="button" className="cart-btn" onClick={() => setCartOpen(true)} aria-label={`Your box, ${summary.quantity} macarons`}>
            <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M4 9h16l-1.4 10.2a2 2 0 0 1-2 1.8H7.4a2 2 0 0 1-2-1.8z" /><path d="M9 9V7a3 3 0 0 1 6 0v2" /></svg>
            <span className="cart-label">Box</span>
            <span className="badge" key={bump}>{summary.quantity}</span>
          </button>
        </div>
      </header>

      <main className="main-content" id="main" tabIndex={-1}>
        {route.page === 'help' ? (
          <HelpPage onBack={() => { window.location.hash = '#/'; }} />
        ) : route.page === 'order' ? (
          <OrderPage key={route.id} id={route.id} justPlaced={justPlaced} catalog={catalog} onBack={() => { window.location.hash = '#/'; }} />
        ) : route.page === 'product' ? (
          shopBody || <ProductPage slug={route.slug} catalog={catalog} cart={cart} onAdd={add} />
        ) : (
          <>
            <section className="hero">
              <img className="hero-photo" src={SCENES.hero} alt="" width="1600" height="900" fetchpriority="high" />
              <div className="hero-copy">
                <p className="hero-kicker">Pâtisserie française · baked to order</p>
                <h1>
                  <span className="line">Little cakes,</span>
                  <span className="line"><em className="script">baked</em> this</span>
                  <span className="line">morning.</span>
                </h1>
                <p className="lead">Crisp almond shells, silky ganache and fruit curds in twelve flavours. Build a box, collect it fresh from our counter.</p>
                <div className="hero-actions">
                  <button type="button" className="btn btn-cream" onClick={scrollToShop}>Build your box <span aria-hidden="true">→</span></button>
                  <button type="button" className="btn btn-ghost" onClick={() => pickCollection('bestseller')}>Our bestsellers</button>
                </div>
              </div>
              <svg className="stamp" viewBox="0 0 120 120" aria-hidden="true">
                <defs><path id="stamp-circle" d="M60 60 m-44 0 a44 44 0 1 1 88 0 a44 44 0 1 1 -88 0" /></defs>
                <circle cx="60" cy="60" r="58" />
                <text><textPath href="#stamp-circle">FRESH EVERY MORNING · FRESH EVERY MORNING · </textPath></text>
                <text x="60" y="70" textAnchor="middle" className="stamp-mid">12</text>
              </svg>
            </section>

            <Ticker className="flavour-ticker" label="Flavours" items={(catalog.length ? catalog : [{ name: 'Raspberry Rose' }, { name: 'Pistachio' }, { name: 'Salted Caramel' }]).map((m) => m.name)} />

            {bestsellers.length > 0 && (
              <section className="spotlight-section reveal" aria-label="Bestsellers">
                <div className="section-head">
                  <h2 className="display">The <em className="script">favourites</em></h2>
                  <p>The three our regulars order again and again.</p>
                </div>
                <Spotlight macarons={bestsellers} cart={cart} onAdd={add} />
              </section>
            )}

            <section id="shop" className="shop" aria-label="Shop">
              <div className="section-head reveal">
                <h2 className="display">Pick your <em className="script">flavours</em></h2>
                <p>Mix and match any twelve flavours: {twelve ? twelve.percent : 10}% off a box of {twelve ? twelve.minQuantity : 12}, {twentyFour ? twentyFour.percent : 15}% off {twentyFour ? twentyFour.minQuantity : 24} or more.</p>
              </div>
              {checkout.pending && (
                <p className="hint" role="status">
                  Your last order was not confirmed. <button type="button" className="link-btn" onClick={() => setCartOpen(true)}>Check it</button>
                </p>
              )}
              {shopBody || (
                <>
                  <Filters filters={filters} setFilters={setFilters} flavors={flavors} count={shown.length} />
                  {filterError && <p className="form-error" role="alert">Search failed: {filterError}</p>}
                  <Grid macarons={shown} cart={cart} onAdd={add} />
                </>
              )}
            </section>

            <section className="gift reveal" aria-label="Gift boxes">
              <div className="gift-photo"><img src={SCENES.giftBox} alt="An open gift box of twelve assorted macarons" loading="lazy" width="1400" height="1050" /></div>
              <div className="gift-copy">
                <p className="kicker">Boxes &amp; gifts</p>
                <h2 className="display">The more you <em className="script">share</em></h2>
                <p>Every box is filled by hand the morning you collect it, wrapped with a ribbon and your note.</p>
                <div className="tiers">
                  <div><strong>{twelve ? twelve.percent : 10}%</strong><span>off a box of {twelve ? twelve.minQuantity : 12}</span></div>
                  <div><strong>{twentyFour ? twentyFour.percent : 15}%</strong><span>off {twentyFour ? twentyFour.minQuantity : 24} or more</span></div>
                </div>
                <button type="button" className="btn btn-dark" onClick={scrollToShop}>Start a box <span aria-hidden="true">→</span></button>
              </div>
            </section>

            <section className="story reveal" aria-label="Our kitchen">
              <div className="story-copy">
                <p className="kicker">Our kitchen</p>
                <h2 className="display">Made by hand, <em className="script">one tray</em> at a time</h2>
                <p>We age our egg whites, sift the almond flour twice and let every shell rest before it meets the oven. Fillings are cooked in small pots: dark chocolate ganache, lemon curd, salted butter caramel.</p>
                <ul className="story-facts">
                  <li><strong>48h</strong><span>aged egg whites</span></li>
                  <li><strong>12</strong><span>flavours, every day</span></li>
                  <li><strong>0</strong><span>shortcuts</span></li>
                </ul>
              </div>
              <div className="story-photo"><img src={SCENES.kitchen} alt="A pastry chef piping macaron shells onto a tray" loading="lazy" width="1080" height="1350" /></div>
            </section>

            <section className="celebrate reveal" aria-label="Celebrations">
              <img src={SCENES.flatlay} alt="" loading="lazy" width="1600" height="900" />
              <div className="celebrate-copy">
                <h2 className="display">Hosting? <em className="script">Make it sweet.</em></h2>
                <p>Order up to {MAX_ORDER_QUANTITY} macarons online for pickup, or talk to us at the counter about towers for weddings and parties.</p>
                <button type="button" className="btn btn-dark" onClick={scrollToShop}>Order for a party <span aria-hidden="true">→</span></button>
              </div>
            </section>
          </>
        )}
      </main>

      {cartOpen && <Cart summary={summary} tiers={tiers} ready={!loading && !initError} checkout={checkout} form={form} setForm={setForm} dispatch={dispatch} onClose={() => setCartOpen(false)} />}

      {showBoxBar && (
        <button type="button" className="mobile-cart-bar" onClick={() => setCartOpen(true)}>
          <span>View box · {summary.quantity} {summary.quantity === 1 ? 'macaron' : 'macarons'}</span>
          <strong>{money(summary.totalCents)}</strong>
        </button>
      )}

      <footer className="footer" id="visit">
        <div className="footer-grid">
          <div className="footer-brand">
            <Brand light />
            <p>French macarons baked to order. Order online, pay and collect at our counter.</p>
          </div>
          <div>
            <h4>Opening hours</h4>
            <p>Tuesday to Saturday, 8:00 to 18:00<br />Sunday, 9:00 to 14:00<br />Closed on Mondays</p>
          </div>
          <div>
            <h4>Pickup</h4>
            <p>Orders are ready from 10:00 on your pickup day. Bring your order number.</p>
          </div>
        </div>
        <p className="footer-word" aria-hidden="true">macarons</p>
        <p className="footer-note">© 2026 La Maison des Macarons · A devkuban.com demo shop · Photography generated with AI</p>
      </footer>
    </div>
  );
}

export default App;
